import { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../layout/loader/Loading";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../httpInterceptor";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  Stack,
  Chip,
  Tooltip,
  LinearProgress,
  Divider,
  Avatar,
  Button,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import PieChartOutlineRoundedIcon from "@mui/icons-material/PieChartOutlineRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import { permissions } from "../../../roles";
import { toast } from "react-toastify";
import { alpha } from "@mui/material/styles";
import { blue } from "@mui/material/colors";
// import newLtimLogo from "../../../images/new.png";
import LTM from "../../../images/LTM.png"
import bg2 from "../../../images/bg2.png"


type SavedStyle = {
  el: HTMLElement;
  prevDisplay: string;
  prevVisibility: string;
  prevWidth: string;
  prevMarginLeft: string;
  prevBackground: string;
};

const likelySidebarSelectors =
  "aside, nav, [role='navigation'], [data-testid*='sidebar'], [data-testid*='nav'], .app-sidebar, .sidebar, aside#sidebar";

function detectSidebars(): HTMLElement[] {
  const list = Array.from(
    document.querySelectorAll<HTMLElement>(likelySidebarSelectors)
  );
  const allNodes = Array.from(
    document.querySelectorAll<HTMLElement>("*")
  ).slice(0, 2000);
  const extended = allNodes.filter((el) => {
    const style = window.getComputedStyle(el);
    const pos = style.position;
    if (!(pos === "fixed" || pos === "sticky")) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const nearLeft = rect.left <= 32;
    const tall = rect.height >= vh * 0.6;
    const notTooWide = rect.width <= vw * 0.45;
    return nearLeft && tall && notTooWide;
  });
  return Array.from(new Set([...list, ...extended]));
}

function detectContentWrappers(): HTMLElement[] {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(
      "main, .content, .app-content, [data-testid*='content'], [role='main'], .app-shell, .layout, .container"
    )
  );
  if (candidates.length === 0) return [];
  const sorted = candidates.sort((a, b) => {
    const ra = a.getBoundingClientRect();
    const rb = b.getBoundingClientRect();
    return rb.width * rb.height - ra.width * ra.height;
  });
  return sorted.slice(0, 5);
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(
    sessionStorage.getItem("PBT_FIRSTNAME")
  );
  const [lastName, setLastName] = useState<string | null>(
    sessionStorage.getItem("PBT_LASTNAME")
  );
  const [userEmail, setUserEmail] = useState<string | null>(
    sessionStorage.getItem("PBT_EMAIL")
  );
  const [userPsNo, setUserPsNo] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const accessToken = sessionStorage.getItem("PBT_ACCESS_TOKEN");

  // Simple in-memory + sessionStorage cache for /userdata to avoid duplicate GETs
  const userDataCacheRef = useRef<any[] | null>(null);

  const fetchUserData = async (force = false) => {
    if (!force) {
      if (userDataCacheRef.current) return userDataCacheRef.current;
      const stored = sessionStorage.getItem("PBT_USERDATA");
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed && parsed.data) {
            userDataCacheRef.current = parsed.data;
            return parsed.data;
          }
        } catch (e) {
          // ignore parse errors and continue to fetch
        }
      }
    }

    const url = `${process.env.REACT_APP_USER_SERVICE_BASE_URL}/userdata`;
    const res = await axiosInstance.get(url);
    const data = res?.data || [];
    userDataCacheRef.current = data;
    try {
      sessionStorage.setItem("PBT_USERDATA", JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {
      // ignore storage errors
    }
    return data;
  };

  const savedStylesRef = useRef<SavedStyle[]>([]);
  const savedRootRef = useRef<Record<string, string>>({});
  const savedHtmlRef = useRef<Record<string, string>>({});
  const savedBodyRef = useRef<Record<string, string>>({});

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.assign(`${process.env.REACT_APP_COGNITOSSO_LOGOUT_URL}`);
  };

  useEffect(() => {
    const html = document.documentElement as HTMLElement;
    const body = document.body as HTMLElement;
    const root = document.getElementById("root") as HTMLElement | null;

    savedHtmlRef.current = {
      background: html.style.background || "",
      backgroundColor: html.style.backgroundColor || "",
    };
    savedBodyRef.current = {
      background: body.style.background || "",
      backgroundColor: body.style.backgroundColor || "",
    };
    if (root) {
      savedRootRef.current = {
        background: root.style.background || "",
        backgroundColor: root.style.backgroundColor || "",
        minHeight: root.style.minHeight || "",
      };
    }

    const lightGradient =
      "linear-gradient(180deg, #F7FAFF 0%, #F2F6FD 60%, #EEF3FB 100%)";
    html.style.background = lightGradient;
    html.style.backgroundColor = "#F5F7FB";
    body.style.background = "transparent";
    body.style.backgroundColor = "transparent";
    if (root) {
      root.style.background = "transparent";
      root.style.backgroundColor = "transparent";
      root.style.minHeight = "100dvh";
    }

    const sidebars = detectSidebars();
    const contents = detectContentWrappers();

    const saved: SavedStyle[] = [];

    sidebars.forEach((el) => {
      saved.push({
        el,
        prevDisplay: el.style.display,
        prevVisibility: el.style.visibility,
        prevWidth: el.style.width,
        prevMarginLeft: el.style.marginLeft,
        prevBackground: el.style.background || "",
      });
      el.style.display = "none";
      el.style.visibility = "hidden";
      el.style.width = "0px";
      el.style.marginLeft = "0px";
      el.style.background = "transparent";
    });

    contents.forEach((el) => {
      saved.push({
        el,
        prevDisplay: el.style.display,
        prevVisibility: el.style.visibility,
        prevWidth: el.style.width,
        prevMarginLeft: el.style.marginLeft,
        prevBackground: el.style.background || "",
      });
      el.style.marginLeft = "0px";
      el.style.background = "transparent";
    });

    savedStylesRef.current = saved;

    return () => {
      savedStylesRef.current.forEach((s) => {
        s.el.style.display = s.prevDisplay;
        s.el.style.visibility = s.prevVisibility;
        s.el.style.width = s.prevWidth;
        s.el.style.marginLeft = s.prevMarginLeft;
        s.el.style.background = s.prevBackground;
      });

      html.style.background = savedHtmlRef.current.background || "";
      html.style.backgroundColor = savedHtmlRef.current.backgroundColor || "";
      body.style.background = savedBodyRef.current.background || "";
      body.style.backgroundColor = savedBodyRef.current.backgroundColor || "";
      if (root && savedRootRef.current) {
        root.style.background = savedRootRef.current.background || "";
        root.style.backgroundColor = savedRootRef.current.backgroundColor || "";
        root.style.minHeight = savedRootRef.current.minHeight || "";
      }
    };
  }, []);

  // Permission check for "home" access (no redirect, just block UI if needed)
  const role = atob(sessionStorage.getItem("key") || "");
  const hasHomePermission = permissions[role?.toUpperCase()]?.includes("home");

  // Fetch user details on component mount if already authenticated
  useEffect(() => {
    const fetchUserDetails = async () => {
      const email = sessionStorage.getItem("PBT_EMAIL");
      if (email && accessToken) {
        try {
          const userData = await fetchUserData();
          const currentUser = userData.find((emp: any) => emp.email === email);
          if (currentUser) {
            setUserPsNo(currentUser.psNo);
            setUserEmail(currentUser.email);
            setUserRole(currentUser.role);
          }
        } catch (err) {
          console.error("Failed to fetch user details:", err);
        }
      }
    };
    fetchUserDetails();
  }, [accessToken]);

  useEffect(() => {
    // Authentication flow
    const runAuthFlow = async () => {
      try {
        const url = new URL(window.location.href);
        const code = url.searchParams.get("code");

        if (!accessToken && !code) {
          window.location.assign(`${process.env.REACT_APP_COGNITOSSO_URL}`);
          return;
        }

        if (code) {
          setAuthLoading(true);
          setError(null);
          sessionStorage.setItem("code", code);

          const tokenResponse = await fetch(
            `https://ltim-de-user-pool.auth.us-east-1.amazoncognito.com/oauth2/token`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: new URLSearchParams({
                grant_type: "authorization_code",
                code,
                client_id: `${process.env.REACT_APP_COGNITO_CLIENT_ID}`,
                redirect_uri: `${process.env.REACT_APP_COGNITO_REDIRECT_URI}`,
              }).toString(),
            }
          );

          if (!tokenResponse.ok) {
            throw new Error(`Token exchange failed (${tokenResponse.status})`);
          }

          const tokens = await tokenResponse.json();
          const { access_token, id_token, refresh_token } = tokens || {};
          if (!id_token || !access_token) {
            throw new Error("Missing tokens in Cognito response.");
          }

          const decoded: any = jwtDecode(id_token);

          sessionStorage.setItem("PBT_ACCESS_TOKEN", access_token);
          sessionStorage.setItem("PBT_REFRESH_TOKEN", refresh_token || "");
          sessionStorage.setItem("PBT_ID_TOKEN", id_token);
          if (decoded?.given_name)
            sessionStorage.setItem("PBT_FIRSTNAME", decoded.given_name);
          if (decoded?.family_name)
            sessionStorage.setItem("PBT_LASTNAME", decoded.family_name);
          if (decoded?.email)
            sessionStorage.setItem("PBT_EMAIL", decoded.email);

          setFirstName(decoded?.given_name ?? null);
          setLastName(decoded?.family_name ?? null);

          // Role fetch -> 'key'
          try {
            const email = decoded?.email;
            if (email) {
              const res = await axiosInstance.get(
                `${
                  process.env.REACT_APP_USER_SERVICE_BASE_URL
                }/getUserRole/${encodeURIComponent(email)}`
              );
              let safeData: string;
              if (res?.data?.message) {
                safeData = btoa("NA");
              } else {
                safeData = btoa(
                  typeof res.data === "string"
                    ? res.data
                    : JSON.stringify(res.data)
                );
              }
              sessionStorage.setItem("key", safeData);

              // Fetch user details including PS ID
              try {
                const userData = await fetchUserData();
                const currentUser = userData.find((emp: any) => emp.email === email);
                if (currentUser) {
                  setUserPsNo(currentUser.psNo);
                  setUserEmail(currentUser.email);
                }
              } catch (userErr) {
                console.error("Failed to fetch user details:", userErr);
              }
            }
          } catch (err) {
            console.error("Role fetch failed:", err);
          }

          // Clean URL
          url.searchParams.delete("code");
          window.history.replaceState({}, document.title, url.toString());
          setAuthLoading(false);
        }
      } catch (e: any) {
        console.error(e);
        setError(e?.message || "Authentication error.");
        setAuthLoading(false);
      }
    };

    runAuthFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = useMemo(
    () => [
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: <DashboardRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Stay updated with live insights and project signals.",
        iconBg: "#5B5B5B", // grey
        buttonText: "View",
        buttonVariant: "contained" as const,
      },
      {
        label: "Add/Modify Questionnaire",
        to: "/create-your-question",
        icon: <AddRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Create and manage questionnaires efficiently.",
        iconBg: "#8E8E8E", // grey
        buttonText: "Manage",
        buttonVariant: "outlined" as const,
      },
      {
        label: "Case Study",
        to: "/case-study",
        icon: <DescriptionRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Build, review, and organize case studies.",
        iconBg: "#C1C1C1", // grey
        buttonText: "Search",
        buttonVariant: "outlined" as const,
      },
      {
        label: "Leadership Report",
        to: "/leadership-report",
        icon: <PieChartOutlineRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Generate structured leadership reports.",
        iconBg: "#8E8E8E", // grey
        buttonText: "View",
        buttonVariant: "outlined" as const,
      },
      {
        label: "Access Management",
        to: "/access-management",
        icon: <GroupRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Manage roles, users and permissions.",
        iconBg: "#5B5B5B", // grey
        buttonText: "Manage",
        buttonVariant: "outlined" as const,
      },
      {
        label: "FAQs",
        to: "/faq",
        icon: <HelpOutlineRoundedIcon sx={{ fontSize: 32 }} />,
        desc: "Find answers to frequently asked questions.",
        iconBg: "#C1C1C1", // grey
        buttonText: "View",
        buttonVariant: "outlined" as const,
      },
    ],
    []
  );

  const go = (path: string) => {
    try {
      const encodedRole = sessionStorage.getItem("key");
      console.log("Raw encoded role from session:", encodedRole);
      
      if (!encodedRole) {
        toast.error("Authentication required. Please log in again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      const role = atob(encodedRole).toUpperCase();
      console.log("Decoded role:", role);
      
      const allowedRoutes = permissions[role];
      console.log("Allowed routes for role:", allowedRoutes);

      // Extract the route name from the path (e.g., "/dashboard" -> "dashboard")
      const routeName = path.startsWith("/") ? path.slice(1) : path;
      
      // Map full route paths to permission names
      const routePermissionMap: Record<string, string> = {
        "create-your-question": "question",
        "leadership-report": "leadership",
        "access-management": "access",
      };
      
      // Get the permission name (use mapped value or routeName itself)
      const permissionName = routePermissionMap[routeName] || routeName;

      console.log("Navigation attempt:", { 
        path, 
        routeName, 
        permissionName, 
        role, 
        allowedRoutes,
        hasPermission: allowedRoutes?.includes(permissionName)
      });

      // Check if role exists in permissions
      if (!allowedRoutes) {
        toast.error(
          `Invalid role: "${role}". Please contact administrator.`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return;
      }

      // Check if the user has permission for this specific route
      if (allowedRoutes.includes(permissionName)) {
        navigate(path);
      } else {
        toast.error(
          `Access denied: Role "${role}" does not have permission to access "${permissionName}"`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    } catch (error) {
      console.error("Navigation error:", error);
      toast.error("Navigation error. Please refresh and try again.", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        background: `url(${bg2}) center/cover no-repeat fixed`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#FFFFFF",
        color: "#1A1A1A",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden"
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          left: 0,
          right: 0,
          top: 0,
          width: "100vw",
          backgroundColor: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(16px) saturate(180%)",
          WebkitBackdropFilter: "blur(16px) saturate(180%)",
          color: "#1A1A1A",
          borderBottom: "1.5px solid rgba(26,26,26,0.08)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03), 0 1px 3px rgba(0,0,0,0.02)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          "@supports (width: 100dvw)": {
            width: "100dvw",
          },
          overflowX: "clip",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar disableGutters sx={{ minHeight: 68 }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: (theme) => theme.breakpoints.values.lg,
              mx: "auto",
              px: { xs: 2, sm: 3 },
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.2}
              sx={{ flex: 1 }}
            >
              {/* <VerifiedUserRoundedIcon sx={{ color: "#2563EB" }} /> */}
              <Box
                component="img"
                src={LTM}
                alt="PBT Application"
                sx={{ height: "1.869", display: "block",width:'7rem' }}
              />
              {/* <Chip
                label="Home"
                size="small"
                sx={{
                  ml: 1,
                  bgcolor: "rgba(37,99,235,0.08)",
                  color: "#1E3A8A",
                  border: "1px solid rgba(37,99,235,0.22)",
                  fontWeight: 700,
                }}
              /> */}
            </Stack>
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                flexShrink: 0, 
                overflow: "visible",
                maxWidth: { xs: "100%", sm: "auto" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.8,
                  px: 2,
                  py: 0.7,
                  borderRadius: 4,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.95) 100%)",
                  border: "1.5px solid rgba(255,94,79,0.18)",
                  boxShadow: "0 4px 14px rgba(255,94,79,0.12), inset 0 1px 2px rgba(255,255,255,0.8)",
                  backdropFilter: "blur(12px)",
                  transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(90deg, transparent, rgba(255,94,79,0.08), transparent)",
                    transition: "left 0.6s ease",
                  },
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(255,94,79,0.22), inset 0 1px 3px rgba(255,255,255,0.9)",
                    borderColor: "rgba(255,94,79,0.35)",
                    transform: "translateY(-2px)",
                    "&::before": {
                      left: "100%",
                    },
                  },
                }}
              >
                <Tooltip
                  title={
                    <Box 
                      sx={{ 
                        p: 1.5, 
                        width: 260,
                        background: '#ffffff',
                        borderRadius: '10px',
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                        position: 'relative',
                        overflow: 'visible',
                      }}
                    >
                      {/* Header with Name */}
                      <Box 
                        sx={{ 
                          pb: 1.5, 
                          mb: 1.5, 
                          borderBottom: '1px solid #e5e7eb',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #FF5E4F 0%, #E54536 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            color: '#fff',
                            boxShadow: '0 3px 10px rgba(255, 94, 79, 0.25)',
                            flexShrink: 0,
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              top: '2px',
                              right: '2px',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: '#10b981',
                              border: '2px solid #ffffff',
                              boxShadow: '0 0 0 1px rgba(16, 185, 129, 0.2)',
                            },
                          }}
                        >
                          {(firstName?.[0] || "U").toUpperCase()}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontSize: "0.9rem", 
                              fontWeight: 600,
                              color: '#111827',
                              lineHeight: 1.3,
                              letterSpacing: '-0.01em',
                              mb: 0.25,
                            }}
                          >
                            {firstName} {lastName}
                          </Typography>
                          {userRole && (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.25,
                                py: 0.4,
                                borderRadius: '5px',
                                background: '#f3f4f6',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: "0.7rem", 
                                  fontWeight: 600,
                                  color: '#6b7280',
                                  letterSpacing: '0.3px',
                                  textTransform: 'uppercase',
                                  lineHeight: 1,
                                }}
                              >
                                {userRole}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Details Section */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {userPsNo && (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              p: 1.25,
                              borderRadius: '8px',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s ease',
                              cursor: 'default',
                              '&:hover': {
                                background: '#f3f4f6',
                                borderColor: '#d1d5db',
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                minWidth: 34,
                                borderRadius: '7px',
                                background: 'linear-gradient(135deg, #FF5E4F 0%, #E54536 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 6px rgba(255, 94, 79, 0.2)',
                              }}
                            >
                              ID
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: "0.65rem", 
                                  fontWeight: 600,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  display: 'block',
                                  lineHeight: 1,
                                  mb: 0.4,
                                }}
                              >
                                PS ID
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.85rem", 
                                  fontWeight: 600,
                                  color: '#111827',
                                  lineHeight: 1,
                                  letterSpacing: '0.2px',
                                }}
                              >
                                {userPsNo}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        
                        {userEmail && (
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              p: 1.25,
                              borderRadius: '8px',
                              background: '#f9fafb',
                              border: '1px solid #e5e7eb',
                              transition: 'all 0.2s ease',
                              cursor: 'default',
                              '&:hover': {
                                background: '#f3f4f6',
                                borderColor: '#d1d5db',
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                minWidth: 34,
                                borderRadius: '7px',
                                background: 'linear-gradient(135deg, #FF5E4F 0%, #E54536 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '1rem',
                                fontWeight: 600,
                                boxShadow: '0 2px 6px rgba(255, 94, 79, 0.2)',
                              }}
                            >
                              @
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: "0.65rem", 
                                  fontWeight: 600,
                                  color: '#6b7280',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  display: 'block',
                                  lineHeight: 1,
                                  mb: 0.4,
                                }}
                              >
                                EMAIL
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontSize: "0.78rem", 
                                  fontWeight: 500,
                                  color: '#111827',
                                  lineHeight: 1.4,
                                  wordBreak: 'break-all',
                                  letterSpacing: '0.1px',
                                }}
                              >
                                {userEmail}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  }
                  arrow
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'transparent',
                        maxWidth: 'none',
                        boxShadow: 'none',
                        '& .MuiTooltip-arrow': {
                          color: '#ffffff',
                          '&::before': {
                            border: '1px solid #e5e7eb',
                          }
                        },
                      },
                    },
                  }}
                >
                  <Avatar
                    alt={firstName ?? "User"}
                    sx={{
                      width: 30,
                      height: 30,
                      fontSize: 14,
                      fontWeight: 500,
                      background: "linear-gradient(135deg, #FF5E4F 0%, #E54536 100%)",
                      boxShadow: "0 2px 8px rgba(255,94,79,0.25)",
                      border: "2px solid #ffffff",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 16px rgba(255,94,79,0.35)",
                      },
                    }}
                  >
                    {(firstName?.[0] || "U").toUpperCase()}
                  </Avatar>
                </Tooltip>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1A1A1A",
                      fontWeight: 600,
                      fontSize: "1rem",
                      maxWidth: 220,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.2,
                      background: "linear-gradient(135deg, #1A1A1A 0%, #282828 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      letterSpacing: "-0.03em",
                    }}
                    title={firstName ? `Hi, ${firstName}` : "Hi"}
                  >
                    Hi, {firstName || "User"}
                  </Typography>
                  {/* {userPsNo && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#475569",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                        }}
                      >
                        PS ID:
                      </Typography>
                      <Chip
                        label={userPsNo}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          bgcolor: "rgba(37,99,235,0.08)",
                          color: "#2563EB",
                          border: "1px solid rgba(37,99,235,0.2)",
                          "& .MuiChip-label": {
                            px: 0.8,
                          },
                        }}
                      />
                    </Box>
                  )} */}
                  {/* {userEmail && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.6,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#475569",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                        }}
                      >
                        Email ID:
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#2563EB",
                          fontSize: "0.7rem",
                          fontWeight: 500,
                          maxWidth: 160,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          lineHeight: 1.2,
                        }}
                        title={userEmail}
                      >
                        {userEmail}
                      </Typography>
                    </Box>
                  )} */}
                </Box>
              </Box>

              <Tooltip title="Logout" arrow placement="bottom">
                <Button
                  onClick={handleLogout}
                  size="medium"
                  variant="outlined"
                  startIcon={<LogoutRoundedIcon />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: "#DC2626",
                    borderColor: "rgba(220,38,38,0.35)",
                    bgcolor: "rgba(255,255,255,0.98)",
                    px: 2.5,
                    py: 1,
                    borderRadius: 3,
                    borderWidth: "1.5px",
                    boxShadow: "0 2px 10px rgba(220,38,38,0.12), inset 0 1px 2px rgba(255,255,255,0.8)",
                    transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
                    position: "relative",
                    overflow: "hidden",
                    "& .MuiButton-startIcon": {
                      mr: 1,
                      transition: "transform 0.3s ease",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: "-100%",
                      width: "100%",
                      height: "100%",
                      background: "linear-gradient(90deg, transparent, rgba(220,38,38,0.08), transparent)",
                      transition: "left 0.5s ease",
                    },
                    "&:hover": {
                      borderColor: "#DC2626",
                      borderWidth: "1.5px",
                      bgcolor: "rgba(254,226,226,0.5)",
                      boxShadow: "0 6px 20px rgba(220,38,38,0.25), inset 0 1px 3px rgba(255,255,255,0.9)",
                      transform: "translateY(-2px) scale(1.02)",
                      "& .MuiButton-startIcon": {
                        transform: "rotate(-15deg) scale(1.1)",
                      },
                      "&::before": {
                        left: "100%",
                      },
                    },
                    "&:active": {
                      transform: "translateY(0) scale(0.98)",
                    },
                  }}
                >
                  Logout
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </Toolbar>
        {authLoading ? <LinearProgress color="primary" /> : null}
      </AppBar>

      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 11, sm: 12, md: 13 },
          pb: { xs: 2, md: 1 },
          translate:'0px -20px'
        }}
      >
        <Stack spacing={1.2} alignItems="flex-start">
          <Typography
            variant="h1"
            sx={(theme) => ({
              fontWeight: 600,
              letterSpacing: '-0.05em',
              lineHeight: 1.1,
              mb: 0.5,
              fontSize: { xs: "1.4rem", sm: "1.4rem", md: "1.4rem" },

              // Base color - Charcoal from brand guidelines
              color: "#1A1A1A",
              textShadow: `0 2px 8px ${alpha("#1A1A1A", 0.08)}`,

              // Smooth transitions
              transition: theme.transitions.create(
                ["color", "text-shadow", "transform"],
                {
                  duration: 160,
                  easing: theme.transitions.easing.easeInOut,
                }
              ),

              "&:hover": {
                color: "#FF5E4F",
                transform: "translateY(-1px)",
                textShadow: `0 6px 18px ${alpha("#FF5E4F", 0.14)}`,
              },
              "&:active": {
                transform: "translateY(0)",
                color: "#E54536",
              },
              "&:focus-visible": {
                outline: "none",
                boxShadow: `0 0 0 4px ${alpha("#FF5E4F", 0.3)}`,
                borderRadius: 6,
              },

              // Motion-safe
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
                "&:hover, &:active": { transform: "none" },
              },
            })}
          >
           PBT (Project Bootstrapping Tool) Assessment & SOW Review Platform
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              color: "#5B5B5B",
              fontWeight: 500,
              fontSize: { xs: "1.05rem", sm: "1.18rem" },
              maxWidth: 'none',
              lineHeight: 1.5,
              letterSpacing: '-0.03em',
              position: "relative",
              display: "inline-block",
              transition: "color 180ms ease",
              whiteSpace: 'nowrap',
              wordBreak: 'keep-all',
              overflow: 'visible',

              "&:hover": { color: "#FF5E4F" },

              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                bottom: -4,
                width: "100%",
                height: 2,
                background: `linear-gradient(90deg, #FF6E61, #FF5E4F)`,
                transform: "scaleX(0)",
                transformOrigin: "left",
                transition: "transform 220ms ease",
                borderRadius: 2,
                boxShadow: `0 1px 6px ${alpha("#FF5E4F", 0.22)}`,
                whiteSpace:'nowrap'
              },
              "&:hover::after": { transform: "scaleX(1)" },
            }}
          >
           Log PBT assessments, review case studies, and generate leadership reports—all in one place.
          </Typography>
        </Stack>
        <Divider sx={{ mt: 3, mb: 2, borderColor: "rgba(193,193,193,0.3)" }} />
      </Container>

      <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 10 }, flex: 1,translate:'0px -23px' }}>
        <Grid container spacing={3}>
          {cards.map((c, index) => (
            <Grid key={`${c.label}-${index}`} item xs={12} sm={6} md={4}>
              <Card
                elevation={0}
                sx={{
                  height: 180,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderRadius: 3,
                  overflow: "visible",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(0, 0, 0, 0.06)",
                  background: "#FFFFFF",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    p: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        minWidth: 56,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: c.iconBg,
                        color: "#FFFFFF",
                        flexShrink: 0,
                      }}
                    >
                      {c.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          color: "#1F2937",
                          mb: 0.5,
                          lineHeight: 1.3,
                        }}
                      >
                        {c.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#6B7280",
                          fontSize: "0.875rem",
                          lineHeight: 1.4,
                        }}
                      >
                        {c.desc}
                      </Typography>
                    </Box>
                  </Stack>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <Button
                      onClick={() => go(c.to)}
                      variant="contained"
                      size="medium"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        px: 3,
                        py: 0.75,
                        borderRadius: 1.5,
                        bgcolor: "#FF5E4F",
                        color: "#FFFFFF",
                        "&:hover": {
                          bgcolor: "#E54536",
                        },
                      }}
                    >
                      {c.buttonText}
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
        {error ? (
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              color: "#7F1D1D",
              bgcolor: "rgba(254, 226, 226, 0.9)",
              border: "1px solid rgba(239,68,68,0.35)",
              p: 1.25,
              borderRadius: 1.5,
            }}
          >
            Authentication Issue: {error}
          </Typography>
        ) : null}
      </Container>

      {authLoading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            display: "grid",
            placeItems: "center",
            backdropFilter: "blur(2px)",
            background: "rgba(15,23,42,0.12)",
            zIndex: 1400,
          }}
        >
          <Box
            sx={{
              width: 420,
              maxWidth: "90vw",
              background: "#FFFFFF",
              border: "1px solid rgba(2,6,23,0.08)",
              borderRadius: 2,
              p: 3,
              color: "#0F172A",
              boxShadow:
                "0 6px 30px rgba(2, 6, 23, 0.15), 0 2px 10px rgba(2, 6, 23, 0.08)",
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Signing you in…
              </Typography>
              <Typography variant="body2" sx={{ color: "#475569" }}>
                Securing your session and preparing your workspace.
              </Typography>
              <LinearProgress color="primary" />
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};
 
export default Home;
 
 

 
 
